import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Paper } from "@mui/material";
import { selectAnnouncementState } from "../../state/announcement/announcementSelector";
import { getAnnouncements, deleteAnnouncement, resetAnnouncementRecipients, getAnnouncementStats, resetAnnouncementStats } from "../../state/announcement/announcementSlice";
import AnnouncementsHeader from "./AnnouncementsHeader";
import AnnouncementsTable from "./AnnouncementsTable";
import CreateEditAnnouncementModal from "./CreateEditAnnouncementModal";
import AnnouncementModalPreview from "./AnnouncementModalPreview";
import AnnouncementStatsModal from "./AnnouncementStatsModal";

const Announcements = () => {
  const dispatch = useDispatch();
  const { data: announcements, count: totalCount, isLoading: loading, stats: statsData, isLoadingStats: loadingStats } = useSelector(selectAnnouncementState);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [selectedLang, setSelectedLang] = useState("en");

  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [statsModalOpen, setStatsModalOpen] = useState(false);

  const fetchAnnouncements = useCallback(() => {
    const params = {
      page: page + 1,
      perPage: rowsPerPage,
    };
    if (statusFilter !== "ALL") {
      params.status = statusFilter;
    }
    dispatch(getAnnouncements(params));
  }, [dispatch, page, rowsPerPage, statusFilter]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const handleCreateNew = () => {
    setSelectedAnnouncement(null);
    setEditModalOpen(true);
  };

  const handleEdit = (announcement) => {
    setSelectedAnnouncement(announcement);
    setEditModalOpen(true);
  };

  const handlePreview = (announcement) => {
    setPreviewData(announcement);
    setPreviewModalOpen(true);
  };

  const handleDelete = (id) => {
    if (!window.confirm("Are you sure you want to delete this announcement?")) return;
    dispatch(deleteAnnouncement(id));
  };

  const handleResetRecipients = (id) => {
    if (!window.confirm("This will clear dismissal history, causing this announcement to reappear for all target users. Continue?")) return;
    dispatch(resetAnnouncementRecipients(id));
  };

  const handleViewStats = (id) => {
    setStatsModalOpen(true);
    dispatch(getAnnouncementStats(id));
  };

  const handleCloseStats = () => {
    setStatsModalOpen(false);
    dispatch(resetAnnouncementStats());
  };

  return (
    <Paper elevation={0}>
      <AnnouncementsHeader
        statusFilter={statusFilter}
        onStatusFilterChange={(val) => {
          setStatusFilter(val);
          setPage(0);
        }}
        selectedLang={selectedLang}
        onLanguageChange={setSelectedLang}
        onCreateNew={handleCreateNew}
      />

      <AnnouncementsTable
        announcements={announcements}
        totalCount={totalCount}
        loading={loading}
        selectedLang={selectedLang}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        onPreview={handlePreview}
        onViewStats={handleViewStats}
        onResetRecipients={handleResetRecipients}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <CreateEditAnnouncementModal open={editModalOpen} onClose={() => setEditModalOpen(false)} announcement={selectedAnnouncement} onSuccess={fetchAnnouncements} />

      <AnnouncementModalPreview open={previewModalOpen} onClose={() => setPreviewModalOpen(false)} announcement={previewData} />

      <AnnouncementStatsModal open={statsModalOpen} onClose={handleCloseStats} stats={statsData} loading={loadingStats} />
    </Paper>
  );
};

export default Announcements;
